import { useCallback, useEffect, useRef, useState } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { InvoiceDetailModel, InvoiceStatus } from '@frontend/api-invoices';
import { HttpError } from '@frontend/fetch';
import { useUpdateInvoiceQueryCache } from '@frontend/payments-invoice-controller';
import {
  ITerminalStrategyError,
  PaymentsTerminalController,
  PaymentsTerminalError,
  StoredReader,
  TerminalReader,
  TerminalStrategyMethods,
  TerminalStrategyProps,
} from '@frontend/payments-terminal-controller';
import { PickPartial, Prettify } from '@frontend/types';
import { useUpdateRequestStatusPoll } from '../../../../hooks/use-update-request-status-poll';
import { useTerminalStatusStoreV2 } from './terminal-status-store-v2';
import { useStartTerminalPaymentV2 } from './use-start-terminal-payment-v2';

export enum ReaderConnectErrors {
  alreadyInUse = 'Reader is currently in use.',
}

export type PaymentIntentInfo = { clientSecret: string; id: string };

type UseTerminalPaymentSessionProps = Prettify<
  PickPartial<
    Omit<TerminalStrategyProps, 'onError' | 'onPaymentStatusChange' | 'enableServerDriven' | 'enableServerDrivenV2'>,
    'reader' | 'sdkFailIfReaderInUse' | 'locationId'
  >
> & {
  person: Person | undefined;
  invoice: InvoiceDetailModel | undefined;
  locationId: string | undefined;
  goBack: () => void;
  discoverReaders: () => Promise<TerminalReader[]>;
  onPaymentFailed?: () => void;
  onPaymentSuccess?: () => void;
  onRefetchInvoice: () => Promise<InvoiceDetailModel | undefined>;
};

type RetryPaymentOptions = {
  failIfReaderInUse: boolean;
  selectedReader: StoredReader;
};

type OptionalProps<T> = Partial<Record<keyof T, undefined>>;
type TightProps<T> = T | OptionalProps<T>;
type ReaderPaymentRetryHandlerOptions = TightProps<{
  selectedReader: StoredReader | undefined;
  failIfReaderInUse: boolean;
}>;

export type ReaderPaymentRetryHandler = (options?: ReaderPaymentRetryHandlerOptions) => void;

/**
 * This hook is used to manage the terminal payment session of both the server-driven and sdk strategies.
 *
 * See [Terminal Payments Flowchart]{@link https://drive.google.com/file/d/1ZHd1HElTDGwqnVPENfrwkXSXqouKHArO/view?usp=sharing}
 */
export const useTerminalPaymentSessionV2 = ({
  reader,
  person,
  locationId,
  appData,
  invoice,
  goBack,
  onPaymentFailed,
  onPaymentSuccess,
  onRefetchInvoice,
  discoverReaders,
}: UseTerminalPaymentSessionProps) => {
  const [isCancelingPayment, setIsCancelingPayment] = useState(false);

  const { startPayment, paymentIntent, isStartingPayment } = useStartTerminalPaymentV2({
    readerId: reader?.readerId,
    person,
    invoice,
    onRefetchInvoice,
    onInvoiceAlreadyPaid: goBack,
  });

  const terminalStatusStore = useTerminalStatusStoreV2({ locationId });

  const activeReader = terminalStatusStore.getActiveReader();

  /**
   * Payment status is processing or success
   */
  const isTerminalStateDisabled = terminalStatusStore.activeReaderMethods.isInRestrictedState();

  /**
   * Props.reader is not the same as active reader
   */
  const isInitializing = terminalStatusStore.isInitializingReader();

  /**
   * Payment was successful
   */
  const isPaymentSuccessful = activeReader?.status === 'success';

  /**
   * Has is initializing collection
   */
  const isStartingCollection = terminalStatusStore.activeReaderMethods.isStartingCollection();

  const mounted = useRef(true);
  const storeOnce = useRef('');

  const { startPoll: startCancelPoll } = useUpdateRequestStatusPoll(invoice, () => {
    // on invoice updated to unpaid status
  });

  const updateInvoiceCache = useUpdateInvoiceQueryCache(invoice?.id);

  const cancelPayment = useCallback(
    async (overrideReaderId?: string) => {
      const readerId = overrideReaderId || terminalStatusStore.getActiveReader()?.reader.readerId;
      if (!readerId) {
        return false;
      }

      const res = await terminalStatusStore.cancelOngoingPayment(readerId);

      const promise = new Promise((resolve) => {
        if (invoice?.status !== InvoiceStatus.Pending) {
          return resolve(res);
        }

        startCancelPoll((latestInvoice) => {
          updateInvoiceCache(latestInvoice);

          resolve(res);
        });
      });

      return promise;
    },
    [invoice?.status]
  );

  const initializePaymentSession = useCallback(
    async (selectedReader = reader, sdkFailIfReaderInUse = true) => {
      if (!selectedReader || !locationId || !appData) {
        return;
      }

      if (terminalStatusStore.isReaderInRestrictedState(selectedReader.readerId)) {
        return;
      }

      terminalStatusStore.setInitializingReaderId(selectedReader.readerId);

      try {
        await terminalStatusStore.cancelAllOngoingPayments();

        if (invoice?.status === InvoiceStatus.Pending) {
          await new Promise((resolve) => {
            if (invoice?.status !== InvoiceStatus.Pending) {
              return resolve(true);
            }

            startCancelPoll((latestInvoice) => {
              updateInvoiceCache(latestInvoice);

              resolve(false);
            });
          });
        }
      } catch (error) {
        console.warn('Error cancelling previous payment before initializing terminal session', { error });
      }

      let newActions: TerminalStrategyMethods;
      try {
        newActions = await PaymentsTerminalController.createTerminalSession({
          locationId,
          reader: selectedReader,
          appData,
          enableServerDriven: true,
          enableServerDrivenV2: true, // can remove this flag when new code is stable
          onPaymentStatusChange(status, staleReaderId) {
            if (!mounted.current) return;

            terminalStatusStore.setReaderStatus(staleReaderId, { status });

            if (status === 'success') {
              onPaymentSuccess?.();
            }
          },
          sdkFailIfReaderInUse,
        });

        await terminalStatusStore.setActiveReader(selectedReader, {
          actions: { ...newActions, readerId: selectedReader.readerId },
        });

        const { paymentIntent, paymentId: paymentWorkflowId } = (await startPayment()) ?? {};

        await terminalStatusStore.setActiveReader(selectedReader, {
          paymentIntentId: paymentIntent?.id,
          actions: { ...newActions, readerId: selectedReader.readerId },
        });

        await newActions.collectAndProcessPayment({
          paymentIntent: {
            id: paymentIntent?.id ?? '',
            clientSecret: paymentIntent?.clientSecret ?? '',
          },
          paymentId: paymentWorkflowId ?? '',
        });
      } catch (error) {
        let terminalError: ITerminalStrategyError = {
          action: 'initialize',
          message: 'An unexpected error occurred',
        };
        if (error instanceof PaymentsTerminalError) {
          terminalError = { action: error.action, message: error.message };
        }
        terminalStatusStore.setReaderStatus(selectedReader.readerId, { status: 'error', error: terminalError });

        terminalStatusStore.setInitializingReaderId(null);

        onPaymentFailed?.();

        return;
      }

      terminalStatusStore.setInitializingReaderId(null);
    },
    [reader, locationId, appData, paymentIntent, invoice?.status]
  );

  const retryPayment = useCallback(
    async ({ failIfReaderInUse, selectedReader }: RetryPaymentOptions) => {
      const targetReader = terminalStatusStore.getReaderState(selectedReader.readerId);
      if (!targetReader) {
        console.warn('retryPayment(): No active reader to retry payment for.');
        return;
      }

      await terminalStatusStore.activeReaderMethods.setForRetryPayment();

      await initializePaymentSession(selectedReader, failIfReaderInUse);
    },
    [initializePaymentSession]
  );

  const handleCancel = useCallback(async () => {
    setIsCancelingPayment(true);
    try {
      await cancelPayment();
    } catch (error) {
      if (
        error instanceof HttpError &&
        typeof error.data === 'object' &&
        error.data?.code === 'mismatch_reader_payment_intent'
      ) {
        console.log('Caught cancel > mismatch error. Going back a step.');
        goBack();
      } else {
        console.warn('Error cancelling payment', { error });
      }
    } finally {
      terminalStatusStore.setInitializingReaderId(null);
      setIsCancelingPayment(false);
      goBack();
    }
  }, [goBack, cancelPayment]);

  const handleTryAgain: ReaderPaymentRetryHandler = useCallback(
    async ({ selectedReader = reader, failIfReaderInUse = true } = {}) => {
      if (!selectedReader) return;

      try {
        const activeReaders = await discoverReaders();

        const foundActiveReader = activeReaders.find((r) => r.id === selectedReader.readerId);
        const potentiallyNewerPaymentIntent = foundActiveReader?.action?.ProcessPaymentIntent?.id;
        if (potentiallyNewerPaymentIntent && potentiallyNewerPaymentIntent !== paymentIntent?.id) {
          await terminalStatusStore.changeReaderPaymentIntentId(selectedReader.readerId, potentiallyNewerPaymentIntent);
        }

        await retryPayment({
          failIfReaderInUse,
          selectedReader,
        });
      } catch (error) {
        console.warn('Error retrying payment', { error });
      }
    },
    [retryPayment, reader, paymentIntent, discoverReaders]
  );

  // Cleanup on unmount
  useEffect(() => {
    mounted.current = true;

    const cancelPaymentOnUnmount = () => {
      cancelPayment(terminalStatusStore.getActiveReader()?.reader.readerId).catch((error) => {
        if (!mounted.current) return;
        console.warn('Error cancelling payment on unmount', { error });

        /**
         * Put any state resetting on error here
         */
      });
    };

    window.addEventListener('beforeunload', cancelPaymentOnUnmount);

    return () => {
      /**
       * Prevent ongoing actions from happening for unmounted component
       */
      mounted.current = false;
      window.removeEventListener('beforeunload', cancelPaymentOnUnmount);

      /**
       * Clear active reader and ongoing payments
       */
      terminalStatusStore.clearAllReaders();
    };
  }, []);

  // Store reader when successful
  useEffect(() => {
    const hasRequiredValues = reader && locationId && appData;
    if (!hasRequiredValues || activeReader?.status !== 'success' || storeOnce.current === reader.readerId) {
      return;
    }
    PaymentsTerminalController.storeReader(locationId, reader);
    storeOnce.current = reader.readerId;
  }, [activeReader?.status, locationId, reader, appData]);

  // INITIATE COLLECTION
  useEffect(() => {
    const terminalActions = activeReader?.actions;
    const hasRequiredValues = !!invoice?.id;

    /**
     * Component double mounts.
     * Must use live check. `isInitializing` is stale in this case
     */
    if (terminalStatusStore.isInitializingReader()) {
      return;
    }

    if (activeReader?.error || !hasRequiredValues) {
      return;
    }

    const isReaderReady = !!reader && !!terminalActions?.readerId;
    const hasChangedReader =
      reader?.readerId && !!activeReader?.reader.readerId && activeReader?.reader.readerId !== reader?.readerId;

    if (hasChangedReader || !isReaderReady) {
      /**
       * Step 1: Initialize terminal session when:
       */
      initializePaymentSession();
    }
  }, [reader?.readerId, activeReader?.reader.readerId, invoice?.id]);

  return {
    readerError: activeReader?.error ?? undefined,
    readerStatus: activeReader?.status ?? 'not_ready',
    terminalActions: activeReader?.actions,
    activeReader,
    isTerminalStateDisabled,
    isLoading: isInitializing || isStartingCollection,
    handleCancel,
    handleTryAgain,
    isPaymentSuccessful,
    isStartingPayment,
    isCancelingPayment,
  };
};
